import React, { useState, useEffect } from 'react';
import { useAuth, auth } from './auth-context';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
    Menu,
    X,
    ArrowLeft,
    ChartBar,
    Brain,
    Target,
    LogOut
} from 'lucide-react';
import { fetchUserStats } from './firebase-utils';

const SessionsHistory = () => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        const loadSessions = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const stats = await fetchUserStats(user.uid);
                setSessions(stats.recentSessions || []);
            } catch (error) {
                console.error('Error loading sessions:', error);
                setError('Failed to load session history. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        loadSessions();
    }, [user]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#070B14] text-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                    <p className="mt-4">Loading session history...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#070B14] text-gray-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6 rounded-2xl bg-white/5 ring-1 ring-white/10">
                    <div className="text-red-400 text-xl mb-4">!</div>
                    <p className="text-gray-300 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#070B14] text-gray-100">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/3 h-[520px] w-[820px] rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute bottom-[-220px] right-[-220px] h-[560px] w-[560px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            {/* Hamburger — only visible when sidebar is closed on mobile */}
            {!isSidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="fixed top-4 left-4 z-50 md:hidden rounded-xl bg-white/10 p-2.5 text-white ring-1 ring-white/10 backdrop-blur"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            )}

            <nav
                className={`fixed top-0 left-0 z-40 h-screen w-full md:w-64 overflow-y-auto md:overflow-hidden md:flex md:flex-col border-r border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl ring-1 ring-inset ring-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_40px_-10px_rgba(6,182,212,0.08)] transition-transform duration-200 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
                    }`}
            >
                <div className="p-6 border-b border-white/10 flex items-start justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-blue-400 mb-2">Security+ Crammer</h1>
                        <div className="flex items-center text-sm text-white/70">
                            Session History
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden rounded-xl bg-white/10 p-2 text-white ring-1 ring-white/10 flex-shrink-0"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="md:flex-grow md:overflow-y-auto p-4">
                    <ul className="space-y-3">
                        <li>
                            <Link to="/reports" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors duration-200">
                                <ChartBar size={18} className="mr-3" />
                                <span className="monospace">Reports</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/practice" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors duration-200">
                                <Target size={18} className="mr-3" />
                                <span className="monospace">Practice</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/flashcards" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors duration-200">
                                <Brain size={18} className="mr-3" />
                                <span className="monospace">Flashcards</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="p-4 px-6 md:px-4 pb-10 md:pb-4 md:border-t border-white/10 space-y-3">
                    <Link to="/" className="block">
                        <button className="w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-3 py-3 text-white flex items-center justify-center">
                            <ArrowLeft size={18} className="mr-3" />
                            <span className="text-sm">Back to Overview</span>
                        </button>
                    </Link>
                    <button onClick={() => signOut(auth)} className="w-full rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-red-500/30 px-3 py-3 text-red-400 flex items-center justify-center text-sm">
                        <LogOut size={18} className="mr-3" />
                        Sign Out
                    </button>
                </div>
            </nav>

            <main className="relative md:ml-64 p-4 md:p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white monospace">All Sessions</h2>
                        <Link to="/reports" className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center">
                            <ArrowLeft size={16} className="mr-1" />
                            Back to Reports
                        </Link>
                    </div>

                    <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
                        <div className="space-y-4">
                            {sessions.length === 0 && (
                                <p className="text-white/55 text-sm">No sessions yet. Start practicing to see your history here.</p>
                            )}
                            {[...sessions].sort((a, b) => new Date(b.date) - new Date(a.date)).map((session, index) => (
                                <div key={index} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-[3px]">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium">
                                                {session.mode.charAt(0).toUpperCase() + session.mode.slice(1)} Session
                                            </div>
                                            <div className="text-sm text-white/70">
                                                {session.questionsAnswered} questions, {session.score.toFixed(1)}% accuracy
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-white/70">
                                                {new Date(session.date).toLocaleDateString()}
                                            </div>
                                            <div className="text-sm text-white/70">
                                                {Math.floor(session.duration / 60)}m {session.duration % 60}s
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SessionsHistory;
